import { Decimal } from './money.js';
const ZERO = new Decimal(0);
/** Погрешность Decimal после деления и корней — на порядки меньше миллиметра. */
const EPS = new Decimal('1e-12');
function heightAt(plane, [x, y]) {
    return plane.c.plus(plane.a.times(x)).plus(plane.b.times(y));
}
/** Площадь многоугольника по формуле Гаусса, без знака. */
export function polygonArea(points) {
    let twice = ZERO;
    for (let i = 0; i < points.length; i++) {
        const [x1, y1] = points[i];
        const [x2, y2] = points[(i + 1) % points.length];
        twice = twice.plus(x1.times(y2).minus(x2.times(y1)));
    }
    return twice.abs().dividedBy(2);
}
/**
 * Отсечь от многоугольника часть, где f(p) > 0 (или ≥ 0 при strict), —
 * алгоритм Сазерленда — Ходжмана для одной полуплоскости. Работает и для
 * невыпуклых многоугольников (Г-образная комната): площадь результата верна.
 */
function clip(polygon, f, strict) {
    const out = [];
    const inside = (value) => (strict ? value.lessThan(0) : value.lessThanOrEqualTo(0));
    for (let i = 0; i < polygon.length; i++) {
        const cur = polygon[i];
        const prev = polygon[(i - 1 + polygon.length) % polygon.length];
        const fc = f(cur);
        const fp = f(prev);
        const inCur = inside(fc);
        const inPrev = inside(fp);
        if (inCur !== inPrev) {
            const t = fp.dividedBy(fp.minus(fc));
            out.push([prev[0].plus(cur[0].minus(prev[0]).times(t)), prev[1].plus(cur[1].minus(prev[1]).times(t))]);
        }
        if (inCur)
            out.push(cur);
    }
    return out;
}
function segmentPlane(segment, input) {
    const a = input.vertices[segment.wall - 1];
    const b = input.wallEnds[segment.wall - 1];
    if (!a || !b)
        return null;
    const dx = b[0].minus(a[0]);
    const dy = b[1].minus(a[1]);
    const length = dx.pow(2).plus(dy.pow(2)).sqrt();
    if (length.isZero())
        return null;
    const [ux, uy] = [dx.dividedBy(length), dy.dividedBy(length)];
    // Внутренняя нормаль: справа по ходу обхода по часовой стрелке (ось y вниз).
    const [nx, ny] = input.clockwise ? [uy.negated(), ux] : [uy, ux.negated()];
    // h = h0 + k·(dist − start), где dist = (p − a)·n — расстояние от стены по полу.
    const k = segment.k;
    const base = a[0].times(nx).plus(a[1].times(ny));
    return {
        c: segment.h0.minus(k.times(segment.start)).minus(k.times(base)),
        a: k.times(nx),
        b: k.times(ny),
        wall: segment.wall,
    };
}
export function roofGeometry(input) {
    const flat = { c: input.height, a: ZERO, b: ZERO, wall: null };
    const planes = [flat, ...input.segments.map((s) => segmentPlane(s, input)).filter((p) => p !== null)];
    // Участок плоскости i — где она ниже всех остальных. При равенстве участок
    // достаётся плоскости с меньшим номером (strict для j < i), иначе две
    // совпавшие плоскости (скат, введённый дважды) посчитались бы дважды.
    const regions = [];
    let ceilingArea = ZERO;
    let slopeArea = ZERO;
    let belowFloor = false;
    planes.forEach((plane, i) => {
        let polygon = input.vertices;
        for (let j = 0; j < planes.length && polygon.length >= 3; j++) {
            if (j === i)
                continue;
            const other = planes[j];
            const [dc, da, db] = [plane.c.minus(other.c), plane.a.minus(other.a), plane.b.minus(other.b)];
            polygon = clip(polygon, ([x, y]) => dc.plus(da.times(x)).plus(db.times(y)), j < i);
        }
        if (polygon.length < 3)
            return;
        const planar = polygonArea(polygon);
        if (planar.lessThanOrEqualTo(EPS))
            return;
        // Наклонная плоскость больше своей проекции в √(1 + |∇h|²) раз.
        const area = planar.times(plane.a.pow(2).plus(plane.b.pow(2)).plus(1).sqrt());
        regions.push({ polygon, plane, area });
        ceilingArea = ceilingArea.plus(area);
        if (plane.wall !== null)
            slopeArea = slopeArea.plus(area);
        if (polygon.some((p) => heightAt(plane, p).lessThan(EPS.negated())))
            belowFloor = true;
    });
    const walls = input.vertices.map((start, i) => {
        const profile = wallProfile(start, input.wallEnds[i], planes);
        if (profile.belowFloor)
            belowFloor = true;
        return { points: profile.points, area: profile.area };
    });
    const wallArea = walls.reduce((sum, w) => sum.plus(w.area), ZERO);
    return { regions, walls, ceilingArea, slopeArea, wallArea, belowFloor };
}
/**
 * Линия потолка вдоль стены. На стене каждая плоскость — прямая h(t), а
 * потолок — нижняя огибающая этих прямых (и не ниже пола). Излом огибающей
 * возможен только там, где пересекаются две прямые, поэтому, посчитав
 * высоту во всех таких точках, получаем ломаную точно и площадь — суммой трапеций.
 */
function wallProfile(start, end, planes) {
    const dx = end[0].minus(start[0]);
    const dy = end[1].minus(start[1]);
    const length = dx.pow(2).plus(dy.pow(2)).sqrt();
    if (length.isZero())
        return { points: [], area: ZERO, belowFloor: false };
    const [ux, uy] = [dx.dividedBy(length), dy.dividedBy(length)];
    const lines = planes.map((p) => ({ s: heightAt(p, start), m: p.a.times(ux).plus(p.b.times(uy)) }));
    const ts = [ZERO, length];
    const addBreak = (t) => {
        if (t.greaterThan(0) && t.lessThan(length))
            ts.push(t);
    };
    for (let i = 0; i < lines.length; i++) {
        const li = lines[i];
        // Пересечение с полом — чтобы обрезать потолок, ушедший ниже нуля.
        if (!li.m.isZero())
            addBreak(li.s.negated().dividedBy(li.m));
        for (let j = i + 1; j < lines.length; j++) {
            const lj = lines[j];
            const dm = li.m.minus(lj.m);
            if (!dm.isZero())
                addBreak(lj.s.minus(li.s).dividedBy(dm));
        }
    }
    ts.sort((a, b) => a.comparedTo(b));
    let belowFloor = false;
    const points = [];
    for (const t of ts) {
        if (points.length > 0 && t.minus(points.at(-1)[0]).lessThanOrEqualTo(EPS))
            continue;
        let h = lines[0].s.plus(lines[0].m.times(t));
        for (const line of lines)
            h = Decimal.min(h, line.s.plus(line.m.times(t)));
        if (h.lessThan(EPS.negated()))
            belowFloor = true;
        points.push([t, Decimal.max(h, ZERO)]);
    }
    // Точки на прямом участке линии не нужны: без них и схема чище, и
    // у стены без ската остаются ровно две точки.
    const simplified = points.filter((p, i) => {
        if (i === 0 || i === points.length - 1)
            return true;
        const [t0, h0] = points[i - 1];
        const [t2, h2] = points[i + 1];
        const expected = h0.plus(h2.minus(h0).times(p[0].minus(t0)).dividedBy(t2.minus(t0)));
        return expected.minus(p[1]).abs().greaterThan(EPS);
    });
    let area = ZERO;
    for (let i = 1; i < simplified.length; i++) {
        const [t0, h0] = simplified[i - 1];
        const [t1, h1] = simplified[i];
        area = area.plus(h0.plus(h1).times(t1.minus(t0)).dividedBy(2));
    }
    return { points: simplified, area, belowFloor };
}
/** Высота линии потолка на расстоянии t от начала стены — для проверки, влезает ли проём. */
export function profileHeightAt(points, t) {
    if (points.length === 0)
        return ZERO;
    if (t.lessThanOrEqualTo(points[0][0]))
        return points[0][1];
    for (let i = 1; i < points.length; i++) {
        const [t1, h1] = points[i];
        if (t.lessThanOrEqualTo(t1)) {
            const [t0, h0] = points[i - 1];
            return h0.plus(h1.minus(h0).times(t.minus(t0)).dividedBy(t1.minus(t0)));
        }
    }
    return points.at(-1)[1];
}
//# sourceMappingURL=roof.js.map